import type { CameraRecording } from '../../../../api/models'
import { VideoProviderName } from '@simplisafe/compass-web'

// One video as the API nests it under a per-camera recording.
type EngagementVideo = NonNullable<CameraRecording['videos']>[number]

export type RecordingDetails = { url: string; provider: VideoProviderName }

// The resolved source the VideoPlayer is (or is about to start) playing. Provider and owning clip ride
// along with the url so the mounted player always describes the source it actually loaded - currentClip
// can move on (e.g. to a clip that errors) while the previous source stays mounted and watchable.
export type PlayableSource = RecordingDetails & { recordingId: string }

// One playable clip, flattened 1:1 from the API's per-camera recording (which nests N videos). The flat
// shape encodes the one-record-one-clip invariant in the type instead of a comment and videos[0] reads.
export type EngagementClip = Pick<EngagementVideo, 'startTime' | 'endTime' | 'links' | 'agentViewStart'> & {
  cameraId: string
  // Deterministic (`${cameraId}:${startTime}`) so it survives recomputation - React keys and per-camera
  // active-clip lookups depend on a stable id.
  recordingId: string
  // The camera-level flag combined with the engagement-level one (museum can deny a single camera).
  accessAllowed: boolean
  disallowedReason?: CameraRecording['disallowedReason']
}

export type EngagementRecordings = {
  recordingsByCameraId: Map<string, EngagementClip[]>
  earliestStartTime: string // ISO timestamp
  latestEndTime: string // ISO timestamp
  // Access mirrors where the API carries it: once for the whole response, and once per camera. Neither is
  // per clip, and a denied camera comes back with no videos at all - so a denial has no clip to travel on
  // and has to be read from here.
  accessAllowed: boolean
  disallowedReason?: CameraRecording['disallowedReason']
  // Denied cameras only. Presence means denied; the value is museum's reason, absent when it sent none.
  deniedReasonByCameraId: Map<string, CameraRecording['disallowedReason']>
}

export enum ViewedIn {
  Main = 'main',
  Mini = 'mini',
  NotAvailable = 'not-available',
  NotViewed = 'not-viewed'
}

export type RecordingViewedDetails = {
  startTimeEpoch: number
  endTimeEpoch: number
  viewedIn: ViewedIn
}
